import { useMemo } from "react";
import { Clock, KeyRound, Star } from "lucide-react";
import { AppCard, CompactAppCard } from "@/components/AppCard";
import { RequestableAppCard } from "@/components/RequestableAppCard";
import { recordLaunch, useApps, useToggleFavourite, type DirectoryApp } from "@/lib/apps";
import { useAppAccess, useAppHealth, type HealthStatus } from "@/lib/directory";
import { filterByAccess, requestableByAccess } from "@/lib/appFilter";

/**
 * Fixed display order; anything unrecognised sorts to the end alphabetically.
 *
 * ⚠ Deliberately not the same order as `CATEGORIES` in
 * `api/src/catalog/seed.ts`: that is the catalog's own grouping, this is what
 * reads best on arrival. Productivity Tools leads because it is the section
 * every employee can actually open. A category missing here still renders,
 * just after the ones listed.
 */
const CATEGORY_ORDER = [
  "Productivity Tools",
  "People & HR",
  "Time & Attendance",
  "Operational Resources",
  "Planning & Analytics",
  "Platform",
];

/**
 * Whether a whole section is open to every employee, so it can be framed and
 * captioned as such.
 *
 * ⚠ Read from each app's own `openToAll` flag rather than from a hard-coded category
 * name, which is what this used to be. The filter reads the same flag, so the caption
 * and the grid cannot disagree — a section captioned "available to every employee"
 * that had quietly hidden one of its cards would be worse than no caption at all.
 * Categories are a display grouping and may be reorganised; the flag is a fact about
 * the app.
 */
const isOpenSection = (apps: DirectoryApp[]): boolean =>
  apps.length > 0 && apps.every((a) => a.openToAll);

function byCategory(a: string, b: string): number {
  const ia = CATEGORY_ORDER.indexOf(a);
  const ib = CATEGORY_ORDER.indexOf(b);
  if (ia === -1 && ib === -1) return a.localeCompare(b);
  if (ia === -1) return 1;
  if (ib === -1) return -1;
  return ia - ib;
}

function Section({
  title,
  icon,
  apps,
  onToggleFavourite,
  onLaunch,
  healthOf,
  compact = false,
  starrable = false,
  framed = false,
  note,
}: {
  title: string;
  icon?: React.ReactNode;
  apps: DirectoryApp[];
  onToggleFavourite: (a: DirectoryApp) => void;
  onLaunch: (a: DirectoryApp) => void;
  healthOf: (id: string) => HealthStatus;
  /** Icon and title only — for apps the user has already chosen or opened. */
  compact?: boolean;
  /** Compact chips keep their star, so a favourite can be undone in place. */
  starrable?: boolean;
  /** Draw the section inside a panel, setting it apart from the plain grids. */
  framed?: boolean;
  /** One line under the heading, explaining what the section is. */
  note?: string;
}) {
  if (apps.length === 0) return null;

  const heading = (
    <div className="mb-3">
      <h2 className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
        {icon}
        {title}
      </h2>
      {note && <p className="text-muted-foreground mt-1.5 text-xs">{note}</p>}
    </div>
  );

  const body = compact ? (
    // Chips flow to their natural width rather than filling grid columns, so a
    // two-app history doesn't stretch into two half-empty cards.
    <div className="flex flex-wrap gap-2">
      {apps.map((app) => (
        <CompactAppCard
          key={app.id}
          app={app}
          onLaunch={onLaunch}
          onToggleFavourite={starrable ? onToggleFavourite : undefined}
        />
      ))}
    </div>
  ) : (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {apps.map((app) => (
        <AppCard
          key={app.id}
          app={app}
          health={healthOf(app.id)}
          onToggleFavourite={onToggleFavourite}
          onLaunch={onLaunch}
        />
      ))}
    </div>
  );

  return (
    <section className="mb-8">
      {framed ? (
        <div className="border-border bg-muted/40 rounded-xl border p-4">
          {heading}
          {body}
        </div>
      ) : (
        <>
          {heading}
          {body}
        </>
      )}
    </section>
  );
}

/**
 * Apps a superadmin opted in that the app says you cannot use — below everything
 * else, because the page is first a launcher for the apps you can open.
 */
function RequestableSection({ apps }: { apps: DirectoryApp[] }) {
  if (apps.length === 0) return null;
  return (
    <section className="mb-8">
      <div className="mb-3">
        <h2 className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
          <KeyRound size={13} />
          Apps you can request
        </h2>
        <p className="text-muted-foreground mt-1.5 text-xs">
          You don’t have access to these yet. Tell the admin team why you need one and
          they’ll be emailed straight away.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {apps.map((app) => (
          <RequestableAppCard key={app.id} app={app} />
        ))}
      </div>
    </section>
  );
}

function CardSkeleton() {
  return (
    <div className="border-border bg-card flex gap-3 rounded-lg border p-4">
      <div className="bg-muted h-11 w-11 shrink-0 animate-pulse rounded-[22%]" />
      <div className="flex-1 space-y-2 py-1">
        <div className="bg-muted h-3.5 w-2/5 animate-pulse rounded" />
        <div className="bg-muted h-3 w-4/5 animate-pulse rounded" />
      </div>
    </div>
  );
}

export function Directory({ query }: { query: string }) {
  const { data: apps, isPending, isError, error } = useApps();
  const toggleFavourite = useToggleFavourite();

  const onToggleFavourite = (app: DirectoryApp) => toggleFavourite.mutate(app);
  const onLaunch = (app: DirectoryApp) => recordLaunch(app.id);

  // Both degrade to neutral when DIRECTORY_TOKEN isn't set or an app hasn't
  // implemented the endpoints — Home must never look broken because a sibling
  // hasn't caught up.
  const { data: healthData } = useAppHealth();
  const { data: accessData } = useAppAccess();
  const healthOf = (id: string): HealthStatus =>
    healthData?.health.find((h) => h.id === id)?.status ?? "unknown";
  const accessOf = (id: string) =>
    accessData ? (accessData.access[id] ?? null) : undefined;

  const filtered = useMemo(() => {
    if (!apps) return [];
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter((a) =>
      [a.title, a.description ?? "", a.category].join(" ").toLowerCase().includes(q),
    );
  }, [apps, query]);

  /**
   * The access filter, applied after the search.
   *
   * ⚠ Depends on `accessData`, not on `accessOf` — that closure is a new function
   * every render, so listing it would recompute this on every keystroke.
   *
   * While the access query is in flight every app reads as "unknown" and nothing is
   * filtered, so the first paint is the full grid and the filter settles a moment
   * later. That order is deliberate: the alternative is holding the whole page back
   * on a second request, and a launcher that shows too much for 200ms is better than
   * one that shows nothing.
   */
  const visible = useMemo(
    () => filterByAccess(filtered, accessOf),
    [filtered, accessData],
  );

  /**
   * The other side of the same verdict: refused apps a superadmin opted in. Only a
   * definitive "no" — while access is loading, or for an app that is not reporting,
   * this is empty and the app stays in the grid above.
   */
  const requestable = useMemo(
    () => requestableByAccess(filtered, accessOf),
    [filtered, accessData],
  );

  const favourites = useMemo(() => visible.filter((a) => a.favourite), [visible]);

  const recent = useMemo(
    () =>
      visible
        .filter((a) => a.lastLaunchedAt && !a.favourite)
        .sort((a, b) => (a.lastLaunchedAt! < b.lastLaunchedAt! ? 1 : -1))
        .slice(0, 3),
    [visible],
  );

  const categories = useMemo(() => {
    const map = new Map<string, DirectoryApp[]>();
    for (const app of visible) {
      const list = map.get(app.category);
      if (list) list.push(app);
      else map.set(app.category, [app]);
    }
    return [...map.entries()].sort(([a], [b]) => byCategory(a, b));
  }, [visible]);

  if (isPending) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <CardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-destructive text-sm">
        Could not load the app directory. {error instanceof Error ? error.message : ""}
      </p>
    );
  }

  if (filtered.length === 0) {
    return (
      <p className="text-muted-foreground py-12 text-center text-sm">
        No apps match “{query}”.
      </p>
    );
  }

  /**
   * Everything the query matched is hidden by the access filter.
   *
   * ⚠ Kept separate from "no apps match", because the two have opposite causes and
   * the wrong message sends someone to re-type a search term that was already right.
   * There is no "show them anyway" here: an app you cannot sign in to is not in the
   * grid, so the honest thing is to say so plainly — and list any that can be
   * requested, which is the one way this page offers to change that.
   */
  if (visible.length === 0) {
    return (
      <>
        <p className="text-muted-foreground py-12 text-center text-sm">
          {query.trim()
            ? `No apps you can sign in to match “${query}”.`
            : requestable.length > 0
              ? "You don’t have access to any apps yet. You can request the ones below."
              : "You don’t have access to any apps yet. Ask your administrator to add you."}
        </p>
        <RequestableSection apps={requestable} />
      </>
    );
  }

  const searching = query.trim().length > 0;

  return (
    <>
      {/* While searching, one flat result list beats sections a query has
          already fragmented into ones and twos. */}
      {searching ? (
        <Section
          title={`${visible.length} result${visible.length === 1 ? "" : "s"}`}
          apps={visible}
          onToggleFavourite={onToggleFavourite}
          onLaunch={onLaunch}
          healthOf={healthOf}
        />
      ) : (
        <>
          <Section
            title="Favourites"
            icon={<Star size={13} />}
            apps={favourites}
            onToggleFavourite={onToggleFavourite}
            onLaunch={onLaunch}
            healthOf={healthOf}
            compact
            starrable
            framed
          />
          <Section
            title="Jump back in"
            icon={<Clock size={13} />}
            apps={recent}
            onToggleFavourite={onToggleFavourite}
            onLaunch={onLaunch}
            healthOf={healthOf}
            compact
            framed
          />
          {categories.map(([category, list]) => (
            <Section
              key={category}
              title={category}
              apps={list}
              onToggleFavourite={onToggleFavourite}
              onLaunch={onLaunch}
              healthOf={healthOf}
              framed={isOpenSection(list)}
              note={
                isOpenSection(list)
                  ? "Available to every employee — nothing to request, just open one."
                  : undefined
              }
            />
          ))}
        </>
      )}
      <RequestableSection apps={requestable} />
    </>
  );
}
